import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CollectionPath, ImainhomeDoc } from '@typings/collections.enum';
import { ImainHome } from '@typings/setting/mainHome.interface';
import Icon from '@components/dataDisplay/Icons/Icon';
import BannerList from './BannerList';

const BannerCollapseWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BannerCollapseBtn = styled.button`
  background-color: var(--buttonBackgroundColor);
  color: var(--buttonTextColor);
  border-radius: var(--buttonBorderRadius);
  transition: background-color 0.2s;
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px 16px;
  font-weight: 700;

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    background-color: var(--inkColor);
  }
`;

const BannerListWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const BannerSectionWrap = styled.div<{ $isBackground: boolean }>`
  width: 100%;
  padding: ${({ $isBackground }) => ($isBackground ? '12px' : '0')};
`;

type Props = { isBannerCollapse?: boolean };
function BannerSec({ isBannerCollapse }: Props) {
  const { data: mainHomeData } = useDocument<ImainHome>(
    `${CollectionPath.MAINHOME}/${ImainhomeDoc.MAINHOME}`,
  );
  const { data: bannerGroupData } = useCollection<IbannerGroup>(
    CollectionPath.BANNER_GROUP,
    {
      listen: true,
      orderBy: ['order', 'asc'],
    },
  );
  const { data: bannerList } = useCollection<Ibanner>(CollectionPath.BANNER, {
    listen: true,
    orderBy: ['order', 'asc'],
  });

  const _data = mainHomeData?.banner;
  const isSectionBackground = _data?.sectionBackgroundSet ?? false;

  const [isCollapse, setIsCollapse] = useState<boolean>(
    isBannerCollapse || _data?.isBannerCollapse || false,
  );

  const toggleCollapse = () => {
    setIsCollapse(!isCollapse);
  };

  const hasBannerGroups = (bannerGroupData?.length || 0) > 0;

  const groupedBanners = useMemo(() => {
    if (!hasBannerGroups) return [];
    return bannerGroupData!.map((group) => ({
      ...group,
      id: group.id as string,
      banners: (bannerList || []).filter(
        (banner) => banner.groupId === group.id,
      ),
    }));
  }, [bannerGroupData, bannerList, hasBannerGroups]);

  const [collapsedMap, setCollapsedMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!bannerGroupData) return;
    setCollapsedMap((prev) => {
      const next = { ...prev };
      bannerGroupData.forEach((group) => {
        const key = group.id as string;
        if (next[key] === undefined) {
          const showCollapseButton =
            (group as IbannerGroup & { showCollapseButton?: boolean })
              .showCollapseButton ?? false;
          next[key] = showCollapseButton ? group.isCollapsed || false : false;
        }
      });
      return next;
    });
  }, [bannerGroupData]);

  const toggleGroup = (groupId: string) => {
    setCollapsedMap((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  if (hasBannerGroups) {
    return (
      <BannerSectionWrap $isBackground={isSectionBackground}>
        {groupedBanners.map((group) => {
          const groupId = group.id as string;
          const collapsed = collapsedMap[groupId] ?? false;
          const showCollapseButton =
            (group as IbannerGroup & { showCollapseButton?: boolean })
              .showCollapseButton ?? false;
          const label =
            group.title || _data?.bannerCollapseText || 'Banner Section';

          return (
            <BannerListWrap key={groupId}>
              {showCollapseButton && (
                <BannerCollapseWrap>
                  <BannerCollapseBtn onClick={() => toggleGroup(groupId)}>
                    <Icon name="ArrowDown12" color="var(--buttonTextColor)" />
                    {label}
                  </BannerCollapseBtn>
                </BannerCollapseWrap>
              )}

              {(!showCollapseButton || !collapsed) && (
                <BannerList banners={group.banners} />
              )}
            </BannerListWrap>
          );
        })}
      </BannerSectionWrap>
    );
  }
  return (
    <BannerSectionWrap $isBackground={isSectionBackground}>
      {(isBannerCollapse || _data?.isBannerCollapse) && (
        <BannerCollapseWrap
          style={{
            marginBottom: isBannerCollapse || _data?.isBannerCollapse ? 16 : 0,
          }}
        >
          <BannerCollapseBtn onClick={toggleCollapse}>
            <Icon name="ArrowDown12" color="var(--buttonTextColor)" />
            {_data?.bannerCollapseText || 'Banner'}
          </BannerCollapseBtn>
        </BannerCollapseWrap>
      )}
      {!isCollapse && <BannerList banners={bannerList || []} />}
    </BannerSectionWrap>
  );
}

export default BannerSec;
